import { isEmpty } from "lodash";
import { Fragment, useEffect, useRef } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { CircularProgress } from "@/Components/v2/atoms/Progress/CircularProgress";
import { Stack } from "@/Components/v2/atoms/Stack";
import NoData from "@/Components/v2/molecule/NoData";
import Header from "@/Components/v2/organism/Header/Header";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useConfig } from "@/hooks/useConfig";
import { Toggle, ToolBarShared } from "@/shared/components/DataGrid/ToolBarShared";
import DataGridContainer from "@/shared/components/DataGridContainer";
import { useDateRangeStore } from "@/shared/components/date-range/store/useDateRangeStore";
import Summary from "@/shared/components/Sections/Summary";
import Title from "@/shared/components/Sections/Title";
import { useMetricOptions } from "@/shared/hooks/useMetricOptions";
import { BusinessUnitToggle, GeoViewToggle, MetricType, ViewToggle } from "@/shared/types";
import { Layer } from "@/Utils/hierarchy";

import { usePublisherROIsV3 } from "./api/getPublisherROIs";
import BreakdownV2 from "./components/Sections/BreakdownV2";
import { content, noDataContent } from "./constants/content";
import { DEFAULT_COLUMNS } from "./constants/defaults";
import { getColumnDefinitions } from "./getColumnDefinitions";
import { usePublishersStore } from "./store/usePublishersStore";
import { useAvailableFilters } from "./useAvailableFilters";

interface PublishersProps {}

const Publishers: React.FC<PublishersProps> = () => {
  const { config } = useConfig();
  const { nodes, loading } = useInfo();
  const { startDate, endDate } = useDateRangeStore();
  const { publishers, channels, targetNode, geographies } = usePublishersStore(
    (state) => state.filters
  );

  const {
    setTargetNode,
    setActiveColumns,
    setGridView,
    setGeoView,
    setBusinessUnitToggle,
    setMetricType,
  } = usePublishersStore();
  const { geoView, gridView, activeColumns, businessUnitToggle, metricType } = usePublishersStore(
    (state) => state.grid
  );
  const { chartView } = usePublishersStore((state) => state.chart);
  const apiRef = useRef(null);

  const currencySymbol = config.currency.getSymbol();
  const formatCurrency = config.currency.formatCurrency;
  const metricOptions = useMetricOptions();

  useEffect(() => {
    if (nodes && !loading && targetNode === null) {
      const defaultNodeUUID = nodes?.getDefaultNode()?.node.uuid;
      if (defaultNodeUUID) {
        setTargetNode(defaultNodeUUID);
      }
    }
  }, [loading, targetNode, nodes, setTargetNode]);

  const { initInfo: info, loading: areGeosLoading } = useInfo();

  const selectedNode = nodes?.getNode(targetNode || "");

  const { data: campaignROIsV3, isLoading: isCampaignsLoading } = usePublisherROIsV3({
    brand: selectedNode?.node.brand ?? "default",
    category: selectedNode?.node.category ?? "default",
    product: selectedNode?.node.product ?? "default",
    channels: channels,
    startDate: startDate ?? "",
    endDate: endDate ?? "",
    graphView: chartView,
    tableView: gridView,
    geoViewToggle: geoView,
    businessUnitToggle: businessUnitToggle,
    publishers,
    geographies: geographies,
    config: {
      enabled:
        !!selectedNode &&
        !!startDate &&
        !!endDate &&
        !!chartView &&
        !!gridView &&
        !!businessUnitToggle,
    },
  });

  const columns = getColumnDefinitions(activeColumns, {
    currencySymbol,
    formatCurrency,
    geoView,
    gridView,
    businessUnitToggle,
    metricType,
    config,
  });

  const availableFilters = useAvailableFilters({
    selectedNode,
    startDate: startDate ?? "",
    endDate: endDate ?? "",
  });

  const { moduleOpened } = useAnalytics();

  useEffect(() => {
    moduleOpened("campaigns", Layer.Portfolio);
  }, []);

  const showWelcomeScreen = !publishers || publishers.length === 0 || channels.length === 0;

  const isLoading = !nodes || loading || isCampaignsLoading || areGeosLoading;

  const toggles: Toggle[] = [
    {
      id: "viewToggle",
      selectedValue: gridView,
      options: [
        { label: "Publishers", value: ViewToggle.INSIGHT },
        { label: "Channels", value: ViewToggle.CHANNEL },
      ],
    },
    {
      id: "businessUnitToggle",
      selectedValue: businessUnitToggle,
      options: [
        { label: "Aggregate Business Units", value: "aggregate" },
        { label: "Split by Business Unit", value: "split" },
      ],
    },
  ];

  const metricDropdownConfig = {
    label: "Show",
    value: metricType,
    options: metricOptions,
    onChange: (value: string | null) => {
      if (value) {
        setMetricType(value as MetricType);
      }
    },
  };

  const [geoData] = info?.geographies || [];

  if (geoData && geoData.length > 0) {
    toggles.push({
      id: "geoViewToggle",
      selectedValue: geoView,
      options: [
        { label: "Aggregate Geographies", value: GeoViewToggle.AGGREGATE },
        { label: "Split by Geography", value: GeoViewToggle.SPLIT },
      ],
    });
  }

  return (
    <Box>
      <Title title={content.title} subtitle={content.subtitle} />
      <Header availableFilters={availableFilters} />
      {isLoading ? (
        <Stack justifyContent="center" alignItems="center" sx={{ minHeight: "50rem" }}>
          <CircularProgress size={150} />
        </Stack>
      ) : isEmpty(campaignROIsV3?.chartView) || isEmpty(campaignROIsV3?.totals) ? (
        <NoData title={noDataContent.title} description={noDataContent.description} />
      ) : (
        <Fragment>
          <Box sx={{ marginX: 4 }}>
            <Summary
              spend={campaignROIsV3?.totals.totalSpend ?? 0}
              revenue={campaignROIsV3?.totals.totalRevenue ?? 0}
              roi={campaignROIsV3?.totals.totalRoi ?? 0}
              formatCurrency={formatCurrency}
            />
            <BreakdownV2
              title={content.title}
              showWelcomeScreen={showWelcomeScreen}
              data={campaignROIsV3?.chartView ?? []}
              totalSpend={campaignROIsV3?.totals.totalSpend ?? 0}
              totalRevenue={campaignROIsV3?.totals.totalRevenue ?? 0}
              currencySymbol={currencySymbol}
            />
            <DataGridContainer
              apiRef={apiRef}
              rows={campaignROIsV3?.tableView ?? []}
              columns={columns}
              metricDropdown={metricDropdownConfig}
              toolbarComponent={
                <ToolBarShared
                  apiRef={apiRef}
                  columns={columns}
                  activeColumns={activeColumns}
                  toggles={toggles}
                  handleApplyFilters={({ activeColumns, toggles }) => {
                    const gridViewToggle = toggles["viewToggle"];
                    const geoViewToggle = toggles["geoViewToggle"];
                    const businessUnitToggle = toggles["businessUnitToggle"];
                    setActiveColumns(activeColumns);
                    setGridView(gridViewToggle as ViewToggle);

                    if (geoViewToggle) {
                      setGeoView(geoViewToggle as GeoViewToggle);
                    }

                    if (businessUnitToggle) {
                      setBusinessUnitToggle(businessUnitToggle as BusinessUnitToggle);
                    }
                  }}
                  handleResetFilters={() => {
                    setActiveColumns(DEFAULT_COLUMNS);
                    setGridView(ViewToggle.INSIGHT);
                    setGeoView(GeoViewToggle.AGGREGATE);
                    setMetricType(MetricType.SALES_UNITS);
                  }}
                />
              }
              options={{
                rowHeight: 52,
                sortModel: [{ field: "total_spend", sort: "desc" }],
              }}
            />
          </Box>
        </Fragment>
      )}
    </Box>
  );
};

export default Publishers;
